/**
 * src/pages/CheckoutPage.js
 * Fixes:
 *  • navigate('/cart') moved into useEffect (was called during render)
 *  • items.map uses i.qty (matches CartContext) not i.quantity
 *  • Field-level API errors (e.g. items[0].product_id) merged correctly
 *  • PRODUCT_OUT_OF_STOCK error code surfaces product name + available stock
 *  • Out-of-stock items highlighted in order summary
 *  • General error banner for non-field errors
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart }   from '../context/CartContext';
import { useAuth }   from '../context/AuthContext';
import { useToast }  from '../context/ToastContext';
import {
  ordersApi, unwrap, ApiError,
  getFieldErrors, getErrorMessage, ErrorCode,
  getImageUrl, getImageUrlFallback,
} from '../services/api';
import { Button, Input, Textarea } from '../components/ui';

// ── Success screen ─────────────────────────────────────────────────────────────
const WA_NUMBER_CHECKOUT = '916374801083'; // same as WhatsAppButton — update this

function SuccessScreen({ orderId, total }) {
  const waMsg = encodeURIComponent(
    `Hi Lakshmi Crackers! I just placed Order #${orderId} for ₹${total}. Please confirm my order and share payment details. Thank you!`
  );
  const waUrl = `https://wa.me/${WA_NUMBER_CHECKOUT}?text=${waMsg}`;

  return (
    <div className="min-h-screen bg-amber-50/50 flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full
        text-center border border-amber-100">

        {/* Green checkmark */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center
          mx-auto mb-5 border-4 border-green-200">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
          </svg>
        </div>

        <h2 className="text-2xl font-black text-gray-900 mb-1">Order Received!</h2>
        <p className="text-gray-400 text-sm mb-5">Thank you for choosing Lakshmi Crackers 🎆</p>

        {/* Order ID — prominent */}
        <div className="bg-red-50 border border-red-200 rounded-2xl px-6 py-4 mb-5">
          <p className="text-xs font-black text-red-400 uppercase tracking-widest mb-1">Your Order ID</p>
          <p className="text-3xl font-black text-red-700"
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            #{orderId}
          </p>
          <p className="text-red-400 text-xs mt-1.5 font-medium">
            📸 Screenshot this — needed for payment confirmation
          </p>
        </div>

        {/* Track Order Note */}
        <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl px-5 py-4 mb-5 text-left">
          <p className="font-black text-amber-800 text-sm mb-1.5">
            💡 Save Your Order ID — Important!
          </p>
          <p className="text-amber-700 text-xs leading-relaxed">
            Your Order ID <span className="font-black text-amber-900">#{orderId}</span> is your
            key to tracking your order anytime. Please <strong>screenshot this page</strong> or
            note it down — you will need it to track your delivery status on our{' '}
            <a href="/track-order"
              className="underline font-black text-red-600 hover:text-red-800">
              Track Order
            </a>{' '}
            page. Don't worry — we will also share it with you on WhatsApp! 🎆
          </p>
        </div>

        {/* Total */}
        <div className="flex items-center justify-between bg-gray-50 rounded-2xl px-5 py-3 mb-5">
          <span className="text-gray-500 text-sm font-semibold">Order Total</span>
          <span className="text-gray-900 font-black text-xl"
            style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            ₹{total}
          </span>
        </div>

        {/* Payment process — clear and honest */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-5 text-left">
          <p className="font-black text-amber-900 text-sm mb-2.5">⚠️ How Payment Works</p>
          <ul className="space-y-2">
            {[
              'This is an enquiry — no payment has been taken yet.',
              'Our team will contact you on WhatsApp or phone within 24 hours.',
              'Payment via UPI, bank transfer, or cash on delivery will be arranged then.',
              'Your order is confirmed only after our team speaks with you.',
            ].map((line, i) => (
              <li key={i} className="text-amber-800 text-xs flex items-start gap-2">
                <span className="text-amber-500 font-black flex-shrink-0 mt-0.5">✓</span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        {/* WhatsApp button — pre-filled message */}
        <a href={waUrl} target="_blank" rel="noreferrer"
          className="flex items-center justify-center gap-2.5 w-full
            bg-[#25D366] hover:bg-[#1da851] text-white font-black
            py-3.5 rounded-2xl transition-colors mb-3 text-sm shadow-md">
          <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5 flex-shrink-0">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
          WhatsApp Us to Confirm Order
        </a>

        <div className="flex gap-3">
          <Link to="/"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-black
              py-3 rounded-2xl transition-colors text-center text-sm">
            🏠 Home
          </Link>
          <Link to="/products"
            className="flex-1 border-2 border-red-200 text-red-600 hover:bg-red-50
              font-black py-3 rounded-2xl transition-colors text-center text-sm">
            🛒 Shop More
          </Link>
        </div>
        <Link to="/track-order"
          className="mt-3 w-full flex items-center justify-center gap-2 bg-amber-50
            border-2 border-amber-300 text-amber-700 hover:bg-amber-100
            font-black py-3 rounded-2xl transition-colors text-sm">
          🔍 Track Your Order
        </Link>

      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function CheckoutPage() {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const { toast }  = useToast();
  const {
    items, coupon,
    subtotal, discountAmount, total,
    clearCart,
  } = useCart();

  const MIN_ORDER_VALUE = 2500;
  const isBelowMinOrder = subtotal < MIN_ORDER_VALUE;

  const [form, setForm] = useState({
    name:    user?.name    || '',
    email:   user?.email   || '',
    phone:   user?.phone   || '',
    address: user?.address || '',
  });
  const [fieldErrors, setFieldErrors]  = useState({});
  const [globalError, setGlobalError]  = useState('');
  const [loading,     setLoading]      = useState(false);
  const [loadingMsg,  setLoadingMsg]   = useState('');

  const LOADING_MSGS = [
    '🧨 Waking up our server from its afternoon nap...',
    '🐢 Asking the database very politely...',
    '🪔 Our crackers are getting excited for you...',
    '☕ Brewing some chai while your order processes...',
    '🎆 Almost there! The server is doing its best impression of speed...',
    '🦥 Render free tier moment — hang tight, boss...',
    '🤔 Our server is thinking... it is a slow thinker...',
    '💃 Your order is dancing its way through the system...',
    '🚀 Launching your order into the database galaxy...',
    '😅 This is totally normal, we promise. Probably.',
  ];

  React.useEffect(() => {
    if (!loading) { setLoadingMsg(''); return; }
    setLoadingMsg(LOADING_MSGS[0]);
    let idx = 1;
    const timer = setInterval(() => {
      setLoadingMsg(LOADING_MSGS[idx % LOADING_MSGS.length]);
      idx++;
    }, 3000);
    return () => clearInterval(timer);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const [success,     setSuccess]      = useState(null);

  // Redirect to cart if empty — done in effect, not in render
  useEffect(() => {
    if (!items.length && !success) navigate('/cart', { replace: true });
  }, [items.length, success, navigate]);

  // Sync form with user object when it loads
  useEffect(() => {
    if (user) {
      setForm(f => ({
        name:    f.name    || user.name    || '',
        email:   f.email   || user.email   || '',
        phone:   f.phone   || user.phone   || '',
        address: f.address || user.address || '',
      }));
    }
  }, [user]);

  if (success) return <SuccessScreen orderId={success.orderId} total={success.total} />;

  const setField = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setFieldErrors(fe => ({ ...fe, [field]: '' }));
    setGlobalError('');
  };

  // ── Client-side validation ─────────────────────────────────────────────────
  const validateClient = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      e.name = 'Enter your full name (at least 2 characters).';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      e.email = 'Enter a valid email address.';
    if (!form.phone.trim() || !/^[6-9]\d{9}$/.test(form.phone.replace(/\D/g, '')))
      e.phone = 'Enter a valid 10-digit Indian mobile number (starts with 6–9).';
    if (!form.address.trim() || form.address.trim().length < 10)
      e.address = 'Enter your complete delivery address (at least 10 characters).';
    return e;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const clientErrs = validateClient();
    if (Object.keys(clientErrs).length) {
      setFieldErrors(clientErrs);
      return;
    }

    setLoading(true);
    setFieldErrors({});
    setGlobalError('');

    const payload = {
      name:        form.name.trim(),
      email:       form.email.trim().toLowerCase(),
      phone:       form.phone.trim().replace(/\D/g, ''),
      address:     form.address.trim(),
      coupon_code: coupon?.code || '',
      // CartContext uses `qty` (fixed from previous version that used `quantity`)
      items: items.map(i => ({
        product_id: i.product.id,
        quantity:   i.qty,
      })),
    };

    try {
      const res  = await ordersApi.place(payload);
      const data = unwrap(res);
      clearCart();
      toast.success('🎆 Order placed successfully!');
      setSuccess({
        orderId: data.order_id,
        total:   parseFloat(data.total).toFixed(0),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        // Out-of-stock: surface specific message
        if (err.code === ErrorCode.PRODUCT_OOS) {
          const detail  = err.detail;
          const prodMsg = detail?.product_name
            ? `"${detail.product_name}" only has ${detail.available_stock} unit(s) left. `
              + 'Update your cart quantity.'
            : err.message;
          setGlobalError(prodMsg);
          toast.error(prodMsg);
          return;
        }

        // Field-level errors from Django serializer
        const fieldErrs = getFieldErrors(err);
        if (Object.keys(fieldErrs).length) {
          setFieldErrors(fieldErrs);
          // Also toast the first error as a summary
          const first = Object.values(fieldErrs)[0];
          if (first) toast.error(first);
          return;
        }

        // Throttled
        if (err.code === ErrorCode.THROTTLED) {
          setGlobalError('Too many requests. Please wait a moment and try again.');
          toast.error('Too many requests. Please wait and try again.');
          return;
        }

        // Network / timeout
        if (err.code === ErrorCode.NETWORK || err.code === ErrorCode.TIMEOUT) {
          setGlobalError(err.message);
          toast.error(err.message);
          return;
        }
      }

      // Generic fallback
      const msg = getErrorMessage(err);
      setGlobalError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!items.length) return null; // redirect in progress

  return (
    <div className="min-h-screen bg-amber-50/50 py-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-black text-gray-900 mb-8">🧾 Checkout</h1>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* ── Delivery form ───────────────────────────────────────────────── */}
          <div className="lg:col-span-3 bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-lg font-black text-gray-800 mb-6">Delivery Details</h2>
            <div className="space-y-5">
              <Input
                label="Full Name" required
                value={form.name} onChange={setField('name')}
                error={fieldErrors.name} placeholder="Priya Ramesh"
                autoComplete="name"
              />
              <Input
                label="Phone Number" required
                value={form.phone} onChange={setField('phone')}
                error={fieldErrors.phone}
                placeholder="10-digit mobile number (e.g. 9876500000)"
                inputMode="numeric"
                autoComplete="tel"
              />
              <Input
                label="Email Address" type="email" required
                value={form.email} onChange={setField('email')}
                error={fieldErrors.email} placeholder="you@example.com"
                autoComplete="email"
              />
              <Textarea
                label="Delivery Address" required rows={3}
                value={form.address} onChange={setField('address')}
                error={fieldErrors.address}
                placeholder="House No, Street, City, State, PIN Code"
              />
            </div>

            {/* Global error (non-field) */}
            {globalError && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700
                text-sm rounded-xl px-4 py-3 flex items-start gap-2">
                <span className="text-lg flex-shrink-0">⚠️</span>
                <span>{globalError}</span>
              </div>
            )}

            {isBelowMinOrder && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
                <p className="text-red-600 font-bold text-sm">
                  ⚠️ Minimum order value is ₹2,500
                </p>
                <p className="text-red-500 text-xs mt-1">
                  Please add ₹{(MIN_ORDER_VALUE - subtotal).toFixed(0)} more to place your order.
                </p>
              </div>
            )}

            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={isBelowMinOrder}
              size="xl"
              className="w-full mt-8"
              icon={loading ? undefined : '✅'}
            >
              {loading ? 'Placing Order…' : 'Place Order'}
            </Button>

            {/* Funny loading message shown while placing order */}
            {loading && loadingMsg && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl
                px-4 py-3 text-center animate-pulse">
                <p className="text-amber-700 text-sm font-semibold leading-relaxed">
                  {loadingMsg}
                </p>
                <p className="text-amber-500 text-xs mt-1">
                  Please don't close this tab — your order is being placed!
                </p>
              </div>
            )}

            {!loading && (
              <p className="text-center text-xs text-gray-400 mt-3">
                🔒 Your details are safe and secure
              </p>
            )}
          </div>

          {/* ── Order summary ────────────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-black text-gray-800 mb-4">Order Summary</h3>
              <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
                {items.map(item => {
                  const img   = getImageUrl(item.product, 80);
                  const imgFb = getImageUrlFallback();

                  // Highlight items with stock issues
                  const stockOk = item.product.stock >= item.qty;

                  return (
                    <div key={item.product.id}
                      className={`flex items-center gap-3 p-2 rounded-xl
                        ${!stockOk ? 'bg-red-50 border border-red-200' : ''}`}>
                      <img
                        src={img}
                        alt={item.product.name}
                        onError={e => { e.target.src = imgFb; }}
                        className="w-12 h-12 rounded-xl object-cover bg-amber-50 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 truncate">
                          {item.product.name}
                        </p>
                        <p className="text-xs text-gray-400">Qty: {item.qty}</p>
                        {!stockOk && (
                          <p className="text-xs text-red-600 font-bold">
                            ⚠ Only {item.product.stock} available
                          </p>
                        )}
                      </div>
                      <p className="text-sm font-black text-gray-900 flex-shrink-0" style={{fontFamily:'"Plus Jakarta Sans",sans-serif'}}>
                        ₹{(parseFloat(item.product.price) * item.qty).toFixed(0)}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-100 mt-4 pt-3 space-y-2 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toFixed(0)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600 font-bold">
                    <span>Discount ({coupon?.code})</span>
                    <span>− ₹{discountAmount.toFixed(0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-500">
                  <span>Delivery</span>
                  <span className="text-amber-700 font-semibold text-xs">Confirmed at order</span>
                </div>
                <div className="flex justify-between font-black text-base
                  border-t border-gray-100 pt-2">
                  <span>Total</span>
                  <span className="text-gray-900 text-xl font-black" style={{fontFamily:'"Plus Jakarta Sans",sans-serif'}}>₹{total.toFixed(0)}</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4">
              <p className="font-black text-amber-900 text-xs uppercase tracking-wider mb-2.5">
                ⚠️ How This Works
              </p>
              <ul className="space-y-1.5">
                {[
                  'This is an enquiry — no payment now.',
                  'We contact you on WhatsApp within 24 hrs.',
                  'Payment via UPI / bank transfer / COD.',
                  'Shipping charges told at confirmation.',
                ].map((line, i) => (
                  <li key={i} className="text-amber-800 text-xs flex items-start gap-1.5">
                    <span className="text-amber-500 font-black flex-shrink-0">✓</span>{line}
                  </li>
                ))}
              </ul>
            </div>

            <Link to="/cart"
              className="block text-center text-xs text-gray-400
                hover:text-red-600 transition-colors">
              ← Edit Cart
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
